import { useQuery, useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { usePageSection } from '@/hooks/usePageContent';
import type { PartnersPageContent } from '@/types/pageContent';
import { DEFAULT_PARTNERS_PAGE } from '../defaults/partnersDefaults';
import { partnersApi } from '../api/partners';
import { SubmitPartnerInquiryRequest, InquirySubmittedResponse, PartnerPageDataResponse } from '../types/partners.types';

export const partnersKeys = {
  all: ['partners'] as const,
  pageData: () => [...partnersKeys.all, 'pageData'] as const,
  categories: () => [...partnersKeys.all, 'categories'] as const,
};

/**
 * Fetch all data needed for the partners landing page
 */
export const usePartnersPageData = () => {
  return useQuery<PartnerPageDataResponse>({
    queryKey: partnersKeys.pageData(),
    queryFn: partnersApi.getPageData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,   // 30 minutes
  });
};

/**
 * Helper hook to organize content sections by key
 */
export const usePartnersPageContent = () => {
  const staticPageContent = usePageSection<PartnersPageContent>('partners', 'content');

  const partnerPageContent = useMemo<PartnersPageContent>(() => {
    return normalizePartnersPageContent(staticPageContent);
  }, [staticPageContent]);

  return { partnerPageContent, isLoading: false, isError: false, error: null };
};

/**
 * Helper hook to get categories and featured partners
 */
export const usePartnersLists = () => {
  const { data, isLoading, isError, error } = usePartnersPageData();

  return {
    categories: data?.categories || [],
    featuredPartners: data?.featuredPartners || [],
    isLoading,
    isError,
    error,
  };
};

/**
 * Mutation for submitting partner inquiries
 */
export const useSubmitPartnerInquiry = () => {
  return useMutation<InquirySubmittedResponse, Error, SubmitPartnerInquiryRequest>({
    mutationFn: partnersApi.submitInquiry,
  });
};

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const getObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? value as Record<string, unknown> : null;

const getString = (value: unknown, fallback: string) =>
  isNonEmptyString(value) ? value : fallback;

const getStringList = (value: unknown, fallback: string[]) => {
  if (!Array.isArray(value)) {
    return [...fallback];
  }

  return fallback.map((defaultValue, index) => {
    const candidate = value[index];
    return isNonEmptyString(candidate) ? candidate : defaultValue;
  });
};

const normalizeHighlight = (
  value: unknown,
  fallback: PartnersPageContent['highlights'][number],
): PartnersPageContent['highlights'][number] => {
  const candidate = getObject(value);
  return {
    title: getString(candidate?.title, fallback.title),
    description: getString(candidate?.description, fallback.description),
  };
};

const normalizePartnersPageContent = (value: unknown): PartnersPageContent => {
  const candidate = getObject(value);
  if (!candidate) {
    return DEFAULT_PARTNERS_PAGE;
  }

  const hero = getObject(candidate.hero);
  const intro = getObject(candidate.intro);
  const logoGroup = getObject(candidate.logoGroup);
  const inquiry = getObject(candidate.inquiry);
  const finalCta = getObject(candidate.finalCta);
  const rawHighlights = Array.isArray(candidate.highlights) ? candidate.highlights : [];

  return {
    hero: {
      eyebrow: getString(hero?.eyebrow, DEFAULT_PARTNERS_PAGE.hero.eyebrow),
      title: getString(hero?.title, DEFAULT_PARTNERS_PAGE.hero.title),
      description: getString(hero?.description, DEFAULT_PARTNERS_PAGE.hero.description),
    },
    intro: {
      title: getString(intro?.title, DEFAULT_PARTNERS_PAGE.intro.title),
      paragraphs: getStringList(intro?.paragraphs, [...DEFAULT_PARTNERS_PAGE.intro.paragraphs]),
    },
    highlights: [
      normalizeHighlight(rawHighlights[0], DEFAULT_PARTNERS_PAGE.highlights[0]),
      normalizeHighlight(rawHighlights[1], DEFAULT_PARTNERS_PAGE.highlights[1]),
      normalizeHighlight(rawHighlights[2], DEFAULT_PARTNERS_PAGE.highlights[2]),
    ],
    logoGroup: {
      title: getString(logoGroup?.title, DEFAULT_PARTNERS_PAGE.logoGroup.title),
      description: getString(logoGroup?.description, DEFAULT_PARTNERS_PAGE.logoGroup.description),
    },
    inquiry: {
      title: getString(inquiry?.title, DEFAULT_PARTNERS_PAGE.inquiry.title),
      description: getString(inquiry?.description, DEFAULT_PARTNERS_PAGE.inquiry.description),
      bulletPoints: getStringList(inquiry?.bulletPoints, [...DEFAULT_PARTNERS_PAGE.inquiry.bulletPoints]),
    },
    finalCta: {
      title: getString(finalCta?.title, DEFAULT_PARTNERS_PAGE.finalCta.title),
      description: getString(finalCta?.description, DEFAULT_PARTNERS_PAGE.finalCta.description),
      callout: getString(finalCta?.callout, DEFAULT_PARTNERS_PAGE.finalCta.callout),
    },
  };
};
